/**
 * @component ErrorBoundary
 * @description Generic React Error Boundary — fängt Render-Crashes ab und zeigt
 *              statt einer weißen Seite ein Fallback-UI mit Retry-Button.
 *              Strukturelle Anti-Whiteout-Maßnahme (#929).
 *
 * @props
 *   - children: ReactNode — die zu schützende Subtree
 *   - fallback: ReactNode — optionales Custom-Fallback statt Default-UI
 *   - onError: (error, errorInfo) => void — optionaler Hook für Logging/Telemetry
 *   - resetKeys: any[] — bei Änderung wird der Boundary-State zurückgesetzt (z.B. URL-Path)
 *   - variant: 'route' | 'panel' — UI-Größe (default 'route')
 *   - label: string — Kontext für Fehlermeldung (z.B. Seitenname), nur Debugging
 *
 * @usage
 *   <ErrorBoundary resetKeys={[location.pathname]}>
 *     <MyPage />
 *   </ErrorBoundary>
 */
import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || '', error, errorInfo);
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, errorInfo); } catch (_) { /* swallow */ }
    }
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.resetKeys;
    const curr = this.props.resetKeys;
    if (this.state.hasError && Array.isArray(prev) && Array.isArray(curr)) {
      const changed = prev.length !== curr.length || prev.some((k, i) => k !== curr[i]);
      if (changed) this.setState({ hasError: false, error: null });
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    if (typeof window !== 'undefined' && window.location) window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const variant = this.props.variant || 'route';
    const isPanel = variant === 'panel';

    // i18n-frei, damit auch ohne LanguageContext sicher renderbar
    const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('weship-language')) === 'de' ? 'de' : 'en';
    const TX = {
      en: {
        title: 'Something went wrong',
        msg: 'This page could not be displayed correctly. Try again, or reload the app.',
        retry: 'Try again',
        reload: 'Reload page',
        details: 'Details',
      },
      de: {
        title: 'Etwas ist schiefgelaufen',
        msg: 'Diese Seite konnte nicht korrekt angezeigt werden. Versuche es erneut oder lade die App neu.',
        retry: 'Erneut versuchen',
        reload: 'Seite neu laden',
        details: 'Details',
      },
    }[lang];

    const errMsg = (this.state.error && (this.state.error.message || String(this.state.error))) || '';

    if (isPanel) {
      return (
        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="font-medium">{TX.title}</p>
              <p className="opacity-80 text-xs mt-0.5">{TX.msg}</p>
              <button
                onClick={this.reset}
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium"
                style={{ background: '#fff', color: '#991B1B', border: '1px solid #FECACA' }}
              >
                <RotateCcw className="w-3 h-3" strokeWidth={2} />
                {TX.retry}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        style={{ background: 'var(--bg-app, #F8F9FC)' }}
      >
        <div
          className="max-w-md w-full rounded-2xl p-6"
          style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: '#FEF2F2' }}
          >
            <AlertTriangle className="w-6 h-6" strokeWidth={1.5} style={{ color: '#DC2626' }} />
          </div>
          <h2 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {TX.title}
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {TX.msg}
          </p>
          {errMsg && (
            <details className="mb-4">
              <summary className="text-xs cursor-pointer select-none" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {TX.details}
              </summary>
              <pre
                className="mt-2 text-[11px] p-2 rounded-lg overflow-auto max-h-40 whitespace-pre-wrap break-words"
                style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-tertiary, #6B7280)' }}
              >
                {errMsg}
              </pre>
            </details>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={this.reset}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium"
              style={{ background: '#338edc', color: '#fff' }}
            >
              <RotateCcw className="w-4 h-4" strokeWidth={2} />
              {TX.retry}
            </button>
            <button
              onClick={this.reload}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-primary, #1A1A2E)', border: '1px solid var(--border-card, #E5E7EB)' }}
            >
              {TX.reload}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
